/**
 * Maison d'Esteban — garda de sincronizare a coșului (sertar + /cart).
 *
 * Bara ofertei, liniile „Cadou” și sumarul sunt randate pe server (secțiunea coșului, prin Section Rendering API).
 * Două capcane (practici §6, §13.4):
 * 1. `cart:lines-update` se emite ÎNAINTE ca mutația să fie comisă → o singură re-citire a /cart.js poate întoarce
 *    coșul vechi; de aceea re-citim până se schimbă semnătura (max. 4 încercări).
 * 2. Pe eroare (429, rețea, răspuns ne-JSON) sertarul poate rămâne cu shimmer și totalul vechi → scoatem shimmer-ul
 *    și re-randăm secțiunea din server.
 * La final comparăm semnătura coșului real cu cea randată (`data-est-cart-sig` = „număr|total”); dacă diferă, re-randăm.
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const signature = (cart) => `${cart.item_count}|${cart.total_price}`;

let modules = null;
function loadModules() {
  if (!modules) {
    modules = Promise.all([
      import('@shopify/events'),
      import('@theme/section-renderer'),
      import('@theme/utilities'),
    ]).catch(() => null);
  }
  return modules;
}

function renderedSignatures() {
  return Array.from(document.querySelectorAll('[data-est-cart-sig]'), (el) => el.getAttribute('data-est-cart-sig'));
}

async function readCart() {
  const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`cart ${response.status}`);
  return response.json();
}

async function resync() {
  const loaded = await loadModules();
  if (!loaded) return;
  const [, { sectionRenderer }, { resetShimmer }] = loaded;
  for (const component of document.querySelectorAll('cart-items-component')) {
    if (!(component instanceof HTMLElement) || !component.dataset.sectionId) continue;
    try {
      resetShimmer(component);
    } catch (_) {
      /* fără shimmer de scos */
    }
    const isDrawer = component.dataset.drawer !== undefined;
    sectionRenderer
      .renderSection(component.dataset.sectionId, {
        cache: false,
        mode: isDrawer ? 'hydration' : 'full',
        injectStylesheet: isDrawer && component.querySelector('[data-cart-drawer-empty]') !== null,
      })
      .catch(() => {});
  }
}

let running = null;
async function verify(before) {
  if (running) return running;
  running = (async () => {
    let cart = null;
    for (let attempt = 0; attempt < 4; attempt++) {
      await sleep(attempt === 0 ? 250 : 450);
      try {
        cart = await readCart();
      } catch (_) {
        continue;
      }
      if (signature(cart) !== before) break;
    }
    if (!cart) return resync();
    // Lăsăm morph-ul temei să termine, apoi comparăm ce e pe ecran cu coșul real.
    await sleep(700);
    const shown = renderedSignatures();
    const real = signature(cart);
    const emptyShown = shown.length === 0;
    if ((cart.item_count > 0 && (emptyShown || shown.some((s) => s !== real))) || (cart.item_count === 0 && !emptyShown)) {
      await resync();
    }
  })().finally(() => {
    running = null;
  });
  return running;
}

loadModules().then((loaded) => {
  if (!loaded) return;
  const [{ StandardEvents }] = loaded;
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const before = renderedSignatures()[0];
    const done = () => verify(before);
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(done, () => {
        resync();
      });
    } else {
      done();
    }
  });
  if (StandardEvents.cartError) {
    document.addEventListener(StandardEvents.cartError, () => resync());
  }
});
